import type { Accept, FileError as DropzoneFileError } from "react-dropzone";

import { runtimeEnv } from "@/core/lib/runtime-env";
import type { ConversationDatasource, Datasource } from "@/openapi";
import { m } from "@/paraglide/messages";

export function getBlobName(
  ds: Datasource | ConversationDatasource | null | undefined,
): string | null {
  if (!ds?.origin) return null;
  return ds.origin.config.type === "blob" ? ds.origin.config.blob_name : null;
}

export function isViewBacked(
  ds: Datasource | ConversationDatasource | null | undefined,
): boolean {
  return ds?.origin?.config.type === "pg_view";
}

const UPLOAD_UUID_PREFIX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;

// uploads are stored as `{org_id}/{uuid}_{file_name}`
export function getUploadedFileName(blobName: string): string {
  const lastSegment = blobName.slice(blobName.lastIndexOf("/") + 1);
  return lastSegment.replace(UPLOAD_UUID_PREFIX, "");
}

export type FileMediaType = "image" | "video" | "other";

export function getFileMediaType(file: File): FileMediaType {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "other";
}

export function getFileExtension(filename: string): string {
  const match = /\.([a-z0-9]+)$/.exec(filename.toLowerCase());
  return match?.[1] || "file";
}

export function getFilenameWithoutExtension(filename: string): string {
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1 || lastDotIndex === 0) return filename;
  return filename.slice(0, lastDotIndex);
}

export function sanitizeFilename(name: string): string {
  return name
    .replaceAll(/[<>:"/\\|?*]/g, "_")
    .replaceAll(/\s+/g, "_")
    .replaceAll(/_+/g, "_")
    .replaceAll(/^_|_$/g, "");
}

export function generateTimestamp(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
}

export const acceptsConfig = {
  excel: {
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel",
    extensions: [".xlsx", ".xls"],
  },
  csv: {
    mimeType: "text/csv",
    extensions: [".csv", ".tsv", ".txt"],
  },
  pdf: {
    mimeType: "application/pdf",
    extensions: [".pdf"],
  },
  xaf: {
    mimeType: "",
    extensions: [".xaf"],
  },
  ipv: {
    mimeType: "",
    extensions: [".ipv"],
  },
  xml: {
    mimeType: "application/xml, text/xml",
    extensions: [".xml"],
  },
} as const;

export type AcceptType = keyof typeof acceptsConfig;

export const MAX_UPLOAD_FILE_SIZE_BYTES = 5 * 1024 * 1024 * 1024;

export function getConfiguredMaxUploadFileSizeBytes(
  configuredValue: string | undefined,
): number {
  if (!configuredValue) {
    return MAX_UPLOAD_FILE_SIZE_BYTES;
  }

  const parsedSize = Number(configuredValue);
  if (!Number.isFinite(parsedSize) || parsedSize <= 0) {
    return MAX_UPLOAD_FILE_SIZE_BYTES;
  }

  return Math.floor(Math.min(parsedSize, MAX_UPLOAD_FILE_SIZE_BYTES));
}

export const buildAcceptConfig = (accepts: AcceptType[]): Accept => {
  return accepts.reduce(
    (acc, key) => {
      const config = acceptsConfig[key];
      // react-dropzone requires plain MIME-type keys: comma-joined strings or
      // bare extensions are skipped with a console warning, dropping the filter
      const mimeTypes = config.mimeType
        ? config.mimeType.split(",").map((mimeType) => mimeType.trim())
        : ["application/octet-stream"];
      for (const mimeType of mimeTypes) {
        acc[mimeType] = [...(acc[mimeType] ?? []), ...config.extensions];
      }
      return acc;
    },
    {} as Record<string, string[]>,
  );
};

export function validateXmlOrNoExtension(file: File): DropzoneFileError | null {
  const hasExtension = file.name.includes(".");
  if (!hasExtension) return null;

  const extension = file.name.split(".").pop()?.toLowerCase();
  if (extension === "xml") return null;

  return {
    code: "invalid-extension",
    message: m.file_upload_invalid_extension({ fileName: file.name }),
  };
}

// ascii alphanumerics, dot, hyphen, underscore and spaces (blocks accents etc.)
export function isValidUploadFilename(name: string): boolean {
  return /^[a-zA-Z0-9._\-\s]+$/.test(name);
}

export function validateUploadFilename(file: File): DropzoneFileError | null {
  return isValidUploadFilename(file.name)
    ? null
    : {
        code: "invalid-filename",
        message: m.toast_file_invalid_characters_description(),
      };
}

export function formatAcceptLabel(accepts: AcceptType[]): string {
  return accepts
    .map((key) => key.charAt(0).toUpperCase() + key.slice(1))
    .join(", ");
}

export function formatAcceptExtensions(accepts: AcceptType[]): string {
  return accepts
    .flatMap((key) =>
      acceptsConfig[key].extensions.map((ext) =>
        ext.replace(".", "").toUpperCase(),
      ),
    )
    .join(", ");
}

export function getMaxUploadFileSize(): number {
  return getConfiguredMaxUploadFileSizeBytes(
    runtimeEnv("VITE_MAX_UPLOAD_FILE_SIZE_BYTES"),
  );
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
